import type { RxJsonSchema } from 'rxdb'

/**
 * Activity Document Type (Denormalized for RxDB)
 */
export interface ActivityDocument {
  id: string
  envId: string
  entityType: string
  entityId: string
  activityType: string
  fieldName: string | null
  description: string | null
  oldValue: string | null
  newValue: string | null
  metadata: string | null
  userId: string
  /** Target user for assignment-style activities (e.g. assignee changed). */
  targetUserId: string | null
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Activity
 */
export const activitySchema: RxJsonSchema<ActivityDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: 'string',
      maxLength: 50,
    },
    entityId: {
      type: 'string',
      maxLength: 36,
    },
    activityType: {
      type: 'string',
      maxLength: 50,
    },
    fieldName: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    description: {
      type: ['string', 'null'],
    },
    oldValue: {
      type: ['string', 'null'],
    },
    newValue: {
      type: ['string', 'null'],
    },
    metadata: {
      type: ['string', 'null'],
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    targetUserId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'entityType',
    'entityId',
    'activityType',
    'userId',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'entityId',
    'createdOn',
    'updatedOn',
    ['entityId', 'isDeleted'],
  ],
}

export const activityMigrationStrategies = {
  // v0 -> v1: backfill `fieldName`, `metadata`, `targetUserId` (added, nullable).
  1: (oldDoc: any): any => ({
    ...oldDoc,
    fieldName: oldDoc.fieldName ?? null,
    metadata: oldDoc.metadata ?? null,
    targetUserId: oldDoc.targetUserId ?? null,
  }),
}
